import { useAuth0 } from '@auth0/auth0-react';
import { isGenuineSessionExpiryError } from '@sunstone/api-client/authBridge';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useState } from 'react';

/**
 * Decoded JWT token structure with Auth0 standard and custom claims
 */
interface DecodedToken {
  /** Standard OIDC claims */
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  'https://api.uniphore.com/auth0_name'?: string;
  nickname?: string;
  picture?: string;

  /** Standard Auth0 organization claims */
  org_id?: string;
  org_name?: string;

  /** OAuth claims */
  scope?: string;
  permissions?: string[];

  /** Custom namespaced claims (from Auth0 custom actions) */
  'https://api.uniphore.com/tenant'?: string;
  'https://api.uniphore.com/user_metadata'?: Record<string, unknown>;

  /** Token metadata */
  iss?: string;
  aud?: string | string[];
  iat?: number;
  exp?: number;
}

/**
 * Auth claims extracted from JWT token
 */
export interface AuthClaims {
  userId: string;
  email: string;
  emailVerified: boolean;
  name: string;
  nickname: string;
  picture: string;
  organizationId: string;
  organizationName: string;
  tenantId: string;
  permissions: string[];
  scope: string;
  userMetadata: Record<string, unknown>;
}

/**
 * Hook to extract and decode Auth0 JWT token claims
 *
 * This hook retrieves the access token from Auth0 and extracts both
 * standard OIDC claims and custom namespaced claims added via Auth0 actions.
 *
 * @returns Object containing decoded claims and loading/error states
 *
 * @example
 * ```tsx
 * const { claims, isLoading, error } = useAuthClaims()
 *
 * if (isLoading) return <Loading />
 * if (error) return <Error message={error} />
 *
 * return <div>Welcome {claims.name} from {claims.organizationName}</div>
 * ```
 */
export function useAuthClaims() {
  const { getAccessTokenSilently, isAuthenticated, isLoading: authLoading } = useAuth0();
  const [claims, setClaims] = useState<AuthClaims | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const extractClaims = async () => {
      if (!isAuthenticated || authLoading) {
        setIsLoading(false);
        return;
      }

      try {
        // Get the access token from Auth0
        const token = await getAccessTokenSilently();
        if (!token) {
          throw new Error('Missing Refresh Token');
        }

        // Decode the JWT token
        const decoded = jwtDecode<DecodedToken>(token);

        // Extract all claims into a structured object
        const extractedClaims: AuthClaims = {
          userId: decoded.sub,
          email: decoded.email || '',
          emailVerified: decoded.email_verified || false,
          name: decoded['https://api.uniphore.com/auth0_name'] || decoded.name || '',
          nickname: decoded.nickname || '',
          picture: decoded.picture || '',
          organizationId: decoded.org_id || '',
          organizationName: decoded.org_name || '',
          tenantId: decoded['https://api.uniphore.com/tenant'] || '',
          permissions: decoded.permissions || [],
          scope: decoded.scope || '',
          userMetadata: decoded['https://api.uniphore.com/user_metadata'] || {},
        };
        setClaims(extractedClaims);
        setError(null);
      } catch (err) {
        console.error('Failed to decode token:', err);

        // Check if this is a session expiration error
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(isGenuineSessionExpiryError(err) ? 'SESSION_EXPIRED' : errorMessage);
        setClaims(null);
      } finally {
        setIsLoading(false);
      }
    };

    extractClaims();
  }, [isAuthenticated, authLoading, getAccessTokenSilently]);

  return {
    claims,
    isLoading,
    error,
  };
}
